import { useState, useEffect, useMemo } from 'react';
import { usePMContext } from '../../contexts/PMContext';
import { useUserContext } from '../../contexts/UserContext';
import { useEquipmentContext } from '../../contexts/EquipmentContext';
import { PMReport } from '../../types/pmTypes';
import { filterReports, ReportFilterCriteria } from '../../utils/pmFilterUtils';
import { fetchPMReportsByCompany } from '../../graphql/pmQueries';
import { Btn, DateField, Pagination, Select } from '../ui';

interface PMReportsContentProps {
    onViewReport: (reportId: string) => void;
}

const PAGE_SIZE = 10;

/**
 * SingleUnit records carry `unitNumber` / `location` at runtime, but the
 * generated Gen2 `SingleUnit` type only exposes `name` / `locationId`.
 * These accessors read the runtime fields without an `any` cast.
 */
type UnitDisplayFields = { id: string; name?: string | null; unitNumber?: string | null; location?: string | null };

function getUnitDisplayName(unit: UnitDisplayFields): string {
    return unit.unitNumber || unit.name || unit.id;
}

function getUnitLocation(unit: UnitDisplayFields): string {
    return unit.location || '';
}

/**
 * PM Reports listing tab.
 * Table listing completed PMReport records: equipment name, service level,
 * date completed, technician name, total labor cost.
 * Filtering by date range, equipment, service level, location.
 * Pagination support.
 *
 * Validates: Requirements 10.1, 10.2, 10.3
 */
export const PMReportsContent = ({ onViewReport }: PMReportsContentProps) => {
    const { pmConfigurations } = usePMContext();
    const { companyUser, companyOwner } = useUserContext();
    const { companyUnits } = useEquipmentContext();

    const [reports, setReports] = useState<PMReport[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);

    // Filter state
    const [dateStart, setDateStart] = useState('');
    const [dateEnd, setDateEnd] = useState('');
    const [selectedEquipmentId, setSelectedEquipmentId] = useState('');
    const [selectedServiceLevelId, setSelectedServiceLevelId] = useState('');
    const [selectedLocationId, setSelectedLocationId] = useState('');

    const companyId = (companyUser?.companyId as string) || companyOwner?.companyId || '';

    // Build equipment name lookup
    const unitNameMap = useMemo(() => {
        const map: Record<string, string> = {};
        companyUnits.forEach((u) => { map[u.id] = getUnitDisplayName(u); });
        return map;
    }, [companyUnits]);

    // Build location lookup
    const locationMap = useMemo(() => {
        const map: Record<string, string> = {};
        companyUnits.forEach((u) => { map[u.id] = getUnitLocation(u); });
        return map;
    }, [companyUnits]);

    // Unique equipment list for filter dropdown
    const equipmentOptions = useMemo(() => {
        const enrolled = pmConfigurations.map(c => c.unitId);
        return companyUnits
            .filter((u) => enrolled.includes(u.id))
            .map((u) => ({ id: u.id, name: getUnitDisplayName(u) }));
    }, [companyUnits, pmConfigurations]);

    // Unique service levels across all configs
    const serviceLevelOptions = useMemo(() => {
        const seen = new Map<string, string>();
        pmConfigurations.forEach(c => {
            (c.serviceLevels ?? []).forEach(sl => {
                if (!seen.has(sl.id)) seen.set(sl.id, sl.levelIdentifier);
            });
        });
        return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
    }, [pmConfigurations]);

    // Unique locations
    const locationOptions = useMemo(() => {
        const locs = new Set<string>();
        companyUnits.forEach((u) => { const loc = getUnitLocation(u); if (loc) locs.add(loc); });
        return Array.from(locs);
    }, [companyUnits]);

    // Fetch reports
    useEffect(() => {
        if (!companyId) return;
        setIsLoading(true);
        fetchPMReportsByCompany(companyId)
            // Raw AppSync records → app PMReport shape; generated field types
            // (Nullable<…>) differ from PMReport, so map at this data boundary.
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .then((items: any[]) => {
                const mapped: PMReport[] = items.map((item) => ({
                    id: item.id,
                    companyId: item.companyId,
                    unitId: item.unitId,
                    pmConfigurationId: item.pmConfigurationId,
                    serviceLevelId: item.serviceLevelId,
                    serviceLevelName: item.serviceLevelName,
                    actualUM: item.actualUM,
                    serviceDate: item.serviceDate,
                    technicianId: item.technicianId,
                    technicianName: item.technicianName,
                    laborRateScheduleId: item.laborRateScheduleId,
                    laborRateScheduleName: item.laborRateScheduleName,
                    laborHoursST: item.laborHoursST,
                    laborHoursOT: item.laborHoursOT,
                    laborRateST: item.laborRateST,
                    laborRateOT: item.laborRateOT,
                    totalLaborCost: item.totalLaborCost,
                    reportData: item.reportData,
                    issueDescription: item.issueDescription,
                    issueTicketCreated: item.issueTicketCreated,
                    issueTicketId: item.issueTicketId,
                    signature: item.signature,
                    photos: item.photos,
                    status: item.status,
                    createdAt: item.createdAt,
                    updatedAt: item.updatedAt,
                }));
                setReports(mapped);
            })
            .catch(err => console.error('Failed to fetch PM reports:', err))
            .finally(() => setIsLoading(false));
    }, [companyId]);

    // Apply filters
    const filteredReports = useMemo(() => {
        const criteria: ReportFilterCriteria = {};
        if (dateStart || dateEnd) {
            criteria.dateRange = {};
            if (dateStart) criteria.dateRange.start = dateStart;
            if (dateEnd) criteria.dateRange.end = dateEnd;
        }
        if (selectedEquipmentId) criteria.equipmentId = selectedEquipmentId;
        if (selectedServiceLevelId) criteria.serviceLevelId = selectedServiceLevelId;
        if (selectedLocationId) criteria.locationId = selectedLocationId;

        const reportsWithLocation = reports.map(r => ({
            ...r,
            locationId: locationMap[r.unitId] || null,
        }));

        return filterReports(reportsWithLocation, criteria);
    }, [reports, dateStart, dateEnd, selectedEquipmentId, selectedServiceLevelId, selectedLocationId, locationMap]);

    // Pagination
    const totalPages = Math.max(1, Math.ceil(filteredReports.length / PAGE_SIZE));
    const paginatedReports = filteredReports.slice(
        (currentPage - 1) * PAGE_SIZE,
        currentPage * PAGE_SIZE,
    );

    // Reset page when filters change
    useEffect(() => { setCurrentPage(1); }, [dateStart, dateEnd, selectedEquipmentId, selectedServiceLevelId, selectedLocationId]);

    const clearFilters = () => {
        setDateStart('');
        setDateEnd('');
        setSelectedEquipmentId('');
        setSelectedServiceLevelId('');
        setSelectedLocationId('');
    };

    const formatDate = (dateStr: string) => {
        if (!dateStr) return '—';
        try { return new Date(dateStr).toLocaleDateString(); } catch { return dateStr; }
    };

    const formatCurrency = (val?: number | null) => {
        if (val == null) return '—';
        return `$${val.toFixed(2)}`;
    };

    if (isLoading) {
        return <div className="pm-table-empty">Loading PM Reports...</div>;
    }

    return (
        <div className="pm-service-queue-panel">
            {/* Filters */}
            <div className="pm-reports-filters">
                <div className="pm-reports-filter-group">
                    <label htmlFor="pm-reports-date-from">Date From</label>
                    <DateField
                        inputId="pm-reports-date-from"
                        className="pm-reports-filter-control"
                        value={dateStart}
                        onChange={setDateStart}
                        placeholder="mm/dd/yyyy"
                        ariaLabel="Date From"
                    />
                </div>
                <div className="pm-reports-filter-group">
                    <label htmlFor="pm-reports-date-to">Date To</label>
                    <DateField
                        inputId="pm-reports-date-to"
                        className="pm-reports-filter-control"
                        value={dateEnd}
                        onChange={setDateEnd}
                        placeholder="mm/dd/yyyy"
                        ariaLabel="Date To"
                    />
                </div>
                <div className="pm-reports-filter-group">
                    <label htmlFor="pm-reports-equipment">Equipment</label>
                    <Select
                        inputId="pm-reports-equipment"
                        className="pm-reports-filter-control"
                        ariaLabel="Equipment"
                        value={selectedEquipmentId}
                        onChange={setSelectedEquipmentId}
                        placeholder="All Equipment"
                        options={[
                            { value: '', label: 'All Equipment' },
                            ...equipmentOptions.map((eq) => ({ value: eq.id, label: eq.name })),
                        ]}
                    />
                </div>
                <div className="pm-reports-filter-group">
                    <label htmlFor="pm-reports-service-level">Service Level</label>
                    <Select
                        inputId="pm-reports-service-level"
                        className="pm-reports-filter-control"
                        ariaLabel="Service Level"
                        value={selectedServiceLevelId}
                        onChange={setSelectedServiceLevelId}
                        placeholder="All Levels"
                        options={[
                            { value: '', label: 'All Levels' },
                            ...serviceLevelOptions.map((sl) => ({ value: sl.id, label: sl.name })),
                        ]}
                    />
                </div>
                <div className="pm-reports-filter-group">
                    <label htmlFor="pm-reports-location">Location</label>
                    <Select
                        inputId="pm-reports-location"
                        className="pm-reports-filter-control"
                        ariaLabel="Location"
                        value={selectedLocationId}
                        onChange={setSelectedLocationId}
                        placeholder="All Locations"
                        options={[
                            { value: '', label: 'All Locations' },
                            ...locationOptions.map((loc) => ({ value: loc, label: loc })),
                        ]}
                    />
                </div>
                <Btn
                    variant="secondary-outlined"
                    buttonSize="sm"
                    buttonText="Clear Filters"
                    buttonOnClick={clearFilters}
                    buttonClasses="pm-clear-filters-btn"
                />
            </div>

            {/* Table */}
            {paginatedReports.length === 0 ? (
                <div className="pm-table-empty">No PM reports found.</div>
            ) : (
                <table className="pm-table">
                    <thead>
                        <tr>
                            <th>Equipment</th>
                            <th>Service Level</th>
                            <th>Date Completed</th>
                            <th>Technician</th>
                            <th>Total Labor Cost</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedReports.map(report => (
                            <tr key={report.id} onClick={() => onViewReport(report.id)}>
                                <td className="pm-equipment-name">{unitNameMap[report.unitId] || report.unitId}</td>
                                <td>{report.serviceLevelName || report.serviceLevelId}</td>
                                <td>{formatDate(report.serviceDate)}</td>
                                <td>{report.technicianName || report.technicianId}</td>
                                <td>{formatCurrency(report.totalLaborCost)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {/* Pagination */}
            {filteredReports.length > PAGE_SIZE && (
                <div className="pm-pagination">
                    <span className="pm-pagination-info">
                        Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filteredReports.length)} of {filteredReports.length}
                    </span>
                    <Pagination
                        className="pm-pagination-controls"
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                        ariaLabel="PM reports pagination"
                    />
                </div>
            )}
        </div>
    );
};
